import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Hero from "./Hero";
import Marquee from "./Marquee";
import MataCantoIntro from "./MataCantoIntro";
import OurPerformers from "./OurPerformers";
import SocialProof from "./SocialProof";
import RecentProductions from "./RecentProductions";
import Services from "./Services";
import ServicesList from "./ServicesList";
import ImmersivePin from "./ImmersivePin";
import EventWorldsScroller from "./EventWorldsScroller";
import ConceptsFan from "./ConceptsFan";
import ServicesConcepts from "./ServicesConcepts";
import Portfolio from "./Portfolio";
import About from "./About";
import WhyMe from "./WhyMe";
import Clients from "./Clients";
import Destinations from "./Destinations";
import CTASection from "./CTASection";
import Contact from "./Contact";
import { FaqSection, GeoLongform } from "./GeoContent";
import Seo from "./Seo";
import { SectionHeader, Reveal, Ornament } from "./Ornament";
import { ArrowUpRight } from "lucide-react";

const useTop = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
};

/* ---------------- MANIFESTO block (new — inspired by reference site) ---------- */
function Manifesto() {
  return (
    <section
      className="relative bg-[var(--lu-ivory)] section-y grain-light"
      data-testid="manifesto-section"
    >
      <div className="container-lux max-w-4xl text-center">
        <p className="kicker text-[var(--lu-gold-dark)]">Our Philosophy</p>
        <Ornament className="my-8" />
        <p className="font-serif italic text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.25] text-[var(--lu-black)]">Bespoke, immersive events — signature.</p>
        <div className="mt-10 space-y-6 text-[17px] leading-[1.9] text-black-70 font-light">
          <p>From the first idea to the finale, we build your own production:
            live music, dancers, aerial &amp; fire shows, themed worlds,
            lighting and complete technical organization — from a single hand.</p>
          <p className="font-serif italic text-[22px] text-[var(--lu-gold-dark)]">You are enjoying the evening. Every detail, ours.</p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOME PAGE (restructured to reference flow) ---------------- */
export function HomePage() {
  useTop();
  const nav = useNavigate();
  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Lumière Entertainment — Luxury Event Entertainment Athens | Performances, Concepts, Production"
        description="Creative atelier producing luxury events in Athens. Bespoke entertainment, themed concepts, golden performers, fire & aerial shows, live music and integrated production for weddings, corporate events, hotels, brand activations and destination events in Greece, Italy, France, Spain, UAE and worldwide."
        keywords="luxury event entertainment Athens, event planner Athens, wedding entertainment Greece, destination wedding entertainment Greece, thematic event concepts, golden performer, aerial show, fire show, corporate entertainment Greece, brand activation, luxury wedding entertainment Santorini Mykonos"
        breadcrumbs={[{ name: "Home", path: "/" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Lumière Entertainment — Home",
          description: "Creative atelier luxury event entertainment in Athens.",
          inLanguage: "el-GR",
          isPartOf: { "@id": "https://lumiereevents.org/#website" },
          about: { "@id": "https://lumiereevents.org/#localbusiness" },
        }}
      />
      <Hero />
      <Marquee />
      <Manifesto />
      <MataCantoIntro />
      <ConceptsFan />
      <OurPerformers />
      <SocialProof />

      {/* Final teaser CTA */}
      <section
        id="home-final-cta"
        className="section-y bg-[var(--lu-black)] grain"
      >
        <div className="container-lux text-center">
          <SectionHeader
            kicker="Your Night, Our Craft"
            title="Let's design together"
            italicTitle="a night of a lifetime."
            light
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-5 justify-center flex-wrap">
            <button
              onClick={() => nav("/contact")}
              className="btn-gold"
              data-testid="home-cta-proposal"
            >
              Request Proposal
            </button>
            <button
              onClick={() => nav("/services")}
              className="btn-gold-outline"
              data-testid="home-cta-services"
            >Explore Services</button>
            <button
              onClick={() => nav("/experiences")}
              className="btn-gold-outline"
              data-testid="home-cta-experiences"
            >See Experiences</button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- SERVICES PAGE ---------------- */
export function ServicesPage() {
  useTop();
  return (
    <div className="pt-32" data-testid="services-page">
      <Seo
        path="/services"
        title="Services Luxury Event Entertainment | Performances, Concepts, Production | Lumière Entertainment"
        description="Comprehensive list of luxury event entertainment services: golden performers, aerial silks, fire performances, oriental shows, live bands, LED costumes, laser choreography, thematic concepts (Sicilian Dolce Vita, Arabian Nights, La Dolce Vita etc.) and comprehensive production for weddings, corporate events, hotels and brand activations in Greece, the Mediterranean and worldwide."
        keywords="event entertainment services, luxury entertainment services Greece, golden performer Athens, aerial silk act, fire performance, live band wedding, thematic concepts, event production Greece"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Luxury Event Entertainment & Production",
          provider: { "@id": "https://lumiereevents.org/#org" },
          areaServed: [
            "Greece", "Italy", "France", "Spain", "United Arab Emirates", "Saudi Arabia", "Worldwide",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Services Lumière Entertainment",
            itemListElement: [
              "Entertainment Design", "Artists & Shows", "Costumes & Styling",
              "Dance Performances", "Thematic Concepts", "Corporate Entertainment", "Music & Party Direction",
            ].map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
          },
        }}
      />
      <div className="container-lux text-center pt-6 pb-4">
        <p className="kicker">Full Catalog</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[96px] text-[var(--lu-black)]">
          Services &amp; <span className="italic text-[var(--lu-gold-dark)]">Concepts</span>
        </h1>
      </div>
      <ServicesList />
      <EventWorldsScroller />
      <ImmersivePin />
      <GeoLongform />
      <FaqSection />
    </div>
  );
}

/* ---------------- PORTFOLIO PAGE (rich) ---------------- */
export function PortfolioFullPage() {
  useTop();
  return (
    <div className="pt-32" data-testid="portfolio-full-page">
      <Seo
        path="/portfolio"
        title="Portfolio — Recent Productions & Signature Nights | Lumière Entertainment"
        description="A curated archive of Lumière productions — private villas in Cap Ferrat, yacht sunsets in Capri, rooftop galas in Athens, palace openings in Riyadh, editor dinners in Milan and more."
      />
      <div className="container-lux text-center pt-6 pb-4">
        <p className="kicker">Our Work</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[96px] text-[var(--lu-black)]">Portfolio &amp;<span className="italic text-[var(--lu-gold-dark)]">Recent Productions</span>
        </h1>
      </div>
      <RecentProductions />
      <Portfolio />
    </div>
  );
}

/* ---------------- ABOUT PAGE (rich) ---------------- */
export function AboutFullPage() {
  useTop();
  return (
    <div className="pt-32" data-testid="about-full-page">
      <Seo
        path="/about"
        title="About — Stamatina Kanda, Founder & Creative Director | Lumière Entertainment"
        description="Stamatina Kanda founded Lumière Entertainment in 2016 to produce cinematic luxury event entertainment in Greece, Italy, France, Spain, UAE and destination worldwide. Ten years of international show production behind every night."
        keywords="Stamatina Kanta, Stamatina Kanta founder, Lumière Entertainment Athens, event creative director Greece, luxury event planner biography, best event planner Athens, best luxury event producer Athens"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            mainEntity: {
              "@type": "Person",
              "@id": "https://lumiereevents.org/#founder-full",
              name: "Stamatina Kanda",
              alternateName: ["Stamatina Kanta", "St. Sing"],
              jobTitle: "Founder & Creative Director",
              worksFor: { "@id": "https://lumiereevents.org/#org" },
              url: "https://lumiereevents.org/about",
              nationality: { "@type": "Country", name: "Greece" },
              homeLocation: { "@type": "Place", name: "Athens, Greece" },
              workLocation: [
                { "@type": "Place", name: "Athens, Greece" },
                { "@type": "Place", name: "Milano, Italy" },
                { "@type": "Place", name: "Cap Ferrat, France" },
                { "@type": "Place", name: "Dubai, UAE" },
                { "@type": "Place", name: "Riyadh, Saudi Arabia" },
              ],
              hasOccupation: {
                "@type": "Occupation",
                name: "Luxury Event Producer & Creative Director",
                occupationLocation: { "@type": "Country", name: "Greece" },
                skills: [
                  "Creative Direction", "Event Production", "Show Choreography",
                  "Costume Design", "Storytelling", "Casting Direction",
                  "Destination Event Logistics", "Brand Activation Design",
                ],
              },
              knowsLanguage: [
                { "@type": "Language", name: "Greek", alternateName: "el" },
                { "@type": "Language", name: "English", alternateName: "en" },
                { "@type": "Language", name: "Italian", alternateName: "it" },
              ],
              knowsAbout: [
                "Luxury Wedding Entertainment",
                "Destination Event Production",
                "Themed Event Concepts",
                "Aerial & Fire Performance",
                "Live Music Direction",
                "Editorial Brand Activations",
                "Bachelorette Editorial Experiences",
                "Mediterranean Cultural Concepts",
                "Minoan & Roman-inspired Weddings",
              ],
              description:
                "Stamatina Kanda is Founder and Creative Director of Lumière Entertainment, a luxury event production atelier based in Athens. With over 10 years in international show production — from Rio Carnival choreographies to residencies in five-star hotels in Dubai — he founded Lumière in 2016 to produce cinematic, bespoke entertainment for weddings, corporate events, hotels, brand activations and destination events in Europe, the Mediterranean, the Middle East and worldwide.",
              sameAs: ["https://instagram.com/stam_kanta"],
              image: "https://lumiereevents.org/generated/hero.jpg",
            },
          },
        ]}
      />
      <div className="container-lux text-center pt-6 pb-4">
        <p className="kicker">Meet Lumière</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[96px] text-[var(--lu-black)]">
          About &amp; <span className="italic text-[var(--lu-gold-dark)]">Philosophy</span>
        </h1>
      </div>
      <About />
      <SocialProof />
      <WhyMe />
      <Clients />
    </div>
  );
}

/* ---------------- RECENT PROJECTS PAGE ---------------- */
export function RecentProjectsPage() {
  useTop();
  return (
    <div className="pt-32" data-testid="recent-projects-page">
      <Seo
        path="/recent-projects"
        title="Recent Projects — Recent Productions | Lumière Entertainment"
        description="A curated archive of recent Lumière Entertainment luxury productions — from private villas in Cap Ferrat, yacht sunsets in Capri, rooftop galas in Athens, palace openings in Riyadh, editor dinners in Milan and destination events across the Mediterranean."
        keywords="recent luxury events, recent productions, event portfolio Athens, luxury event photos Greece, destination event examples"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Recent Projects", path: "/recent-projects" },
        ]}
      />
      <div className="container-lux text-center pt-6 pb-4">
        <p className="kicker">Our Archive</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[96px] text-[var(--lu-black)]">
          Recent <span className="italic text-[var(--lu-gold-dark)]">Projects</span>
        </h1>
      </div>
      <RecentProductions />
      <Portfolio />
    </div>
  );
}

/* ---------------- CONTACT PAGE ---------------- */
export function ContactPage() {
  useTop();
  return (
    <div className="pt-32" data-testid="contact-page">
      <Seo
        path="/contact"
        title="Contact — Request Bespoke Offer | Lumière Entertainment Athens"
        description="Contact Stamatina Kanda (Founder) directly for bespoke luxury entertainment in Greece, Italy, France, Spain, UAE and destination worldwide. Phone +30 694 316 7877, email stamatina.kanta@yahoo.com. Response within 24 hours."
        keywords="Lumière Entertainment contact, event entertainment quote Athens, book luxury performer Greece, bespoke event proposal"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          mainEntity: { "@id": "https://lumiereevents.org/#localbusiness" },
        }}
      />
      <div className="container-lux text-center pt-6 pb-4">
        <p className="kicker">Let's Get Started</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[96px] text-[var(--lu-black)]">Get in touch<span className="italic text-[var(--lu-gold-dark)]">With Us</span>
        </h1>
      </div>
      <CTASection />
      <Contact />
    </div>
  );
}
